import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services';
import type { DashboardStats, SiteDashboard } from '../../services/dashboard.service';
import { CheckInOut } from '../../components/CheckInOut/CheckInOut';
import { USER_ROLES } from '../../constants';
import styles from './Dashboard.module.css';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [sites, setSites] = useState<SiteDashboard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const statsResponse = await dashboardService.getStats();
        setStats(statsResponse);

        // Per Owner/SuperAdmin carica anche dati per sede
        if (user && user.role <= USER_ROLES.OWNER) {
          const companyResponse = await dashboardService.getCompanyDashboard();
          setSites(companyResponse.sites);
        }
      } catch (err) {
        console.error('Errore caricamento dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [user]);

  const getRoleDescription = () => {
    switch (user?.role) {
      case 0:
        return 'Hai accesso completo a tutte le funzionalita\u0300 del sistema';
      case 1:
        return 'Puoi gestire la tua azienda, le sedi e i dipendenti';
      case 2:
        return 'Puoi gestire i turni e i dipendenti della tua sede';
      case 3:
        return 'Puoi visualizzare i tuoi turni e fare richieste';
      default:
        return '';
    }
  };

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h1>Benvenuto, {user?.firstName}!</h1>
        <p className={styles.subtitle}>{getRoleDescription()}</p>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statInfo}>
            <h3>Turni Oggi</h3>
            <p className={styles.statValue}>
              {loading ? '...' : stats?.shiftsToday ?? 0}
            </p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statInfo}>
            <h3>Ore Lavorate (Mese)</h3>
            <p className={styles.statValue}>
              {loading ? '...' : `${stats?.hoursWorkedThisMonth ?? 0}h`}
            </p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statInfo}>
            <h3>Richieste Pendenti</h3>
            <p className={styles.statValue}>
              {loading ? '...' : stats?.pendingRequests ?? 0}
            </p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statInfo}>
            <h3>Notifiche</h3>
            <p className={styles.statValue}>
              {loading ? '...' : stats?.unreadNotifications ?? 0}
            </p>
          </div>
        </div>
      </div>

      {/* Check-In/Out per Employee */}
      {user && user.role === USER_ROLES.EMPLOYEE && (
        <div className={styles.checkInOutSection}>
          <CheckInOut />
        </div>
      )}

      {/* Panoramica sedi per Owner/SuperAdmin */}
      {user && user.role <= USER_ROLES.OWNER && sites.length > 0 && (
        <div className={styles.sitesOverview}>
          <div className={styles.sitesHeader}>
            <h2>Panoramica Sedi</h2>
            <button
              className={styles.manageSitesButton}
              onClick={() => navigate('/sites')}
            >
              Gestisci Sedi
            </button>
          </div>
          <div className={styles.sitesGrid}>
            {sites.map((site) => (
              <div key={site.siteId} className={styles.siteCard}>
                <div className={styles.siteCardHeader}>
                  <h3>{site.siteName}</h3>
                  <span className={styles.siteCity}>{site.city}</span>
                </div>
                {site.managerName && (
                  <p className={styles.siteManager}>Manager: {site.managerName}</p>
                )}
                <div className={styles.siteStats}>
                  <div className={styles.siteStat}>
                    <span className={styles.siteStatValue}>{site.shiftsToday}</span>
                    <span className={styles.siteStatLabel}>Turni oggi</span>
                  </div>
                  <div className={styles.siteStat}>
                    <span className={styles.siteStatValue}>{site.activeEmployees}</span>
                    <span className={styles.siteStatLabel}>Dipendenti</span>
                  </div>
                  <div className={styles.siteStat}>
                    <span className={styles.siteStatValue}>{site.pendingRequests}</span>
                    <span className={styles.siteStatLabel}>Richieste</span>
                  </div>
                  <div className={styles.siteStat}>
                    <span className={styles.siteStatValue}>{site.hoursWorkedThisMonth}h</span>
                    <span className={styles.siteStatLabel}>Ore mese</span>
                  </div>
                </div>
                {site.shiftsInProgress > 0 && (
                  <div className={styles.siteActive}>
                    {site.shiftsInProgress} turni in corso
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.quickActions}>
        <h2>Azioni Rapide</h2>
        <div className={styles.actionsGrid}>
          <button className={styles.actionButton} onClick={() => navigate('/shifts')}>
            Visualizza Turni
          </button>

          {user && user.role === USER_ROLES.EMPLOYEE && (
            <button className={styles.actionButton} onClick={() => navigate('/requests')}>
              Nuova Richiesta
            </button>
          )}

          {user && user.role <= USER_ROLES.MANAGER && (
            <>
              <button className={styles.actionButton} onClick={() => navigate('/shifts')}>
                Crea Turno
              </button>
              <button className={styles.actionButton} onClick={() => navigate('/users')}>
                Gestisci Utenti
              </button>
            </>
          )}
        </div>
      </div>

      <div className={styles.infoSection}>
        <h2>Informazioni Account</h2>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <strong>Nome:</strong> {user?.fullName}
          </div>
          <div className={styles.infoItem}>
            <strong>Email:</strong> {user?.email}
          </div>
          <div className={styles.infoItem}>
            <strong>Ruolo:</strong>{' '}
            {user?.role === 0 && 'SuperAdmin'}
            {user?.role === 1 && 'Owner'}
            {user?.role === 2 && 'Manager'}
            {user?.role === 3 && 'Employee'}
          </div>
          {user?.companyName && (
            <div className={styles.infoItem}>
              <strong>Azienda:</strong> {user.companyName}
            </div>
          )}
          {user?.siteName && (
            <div className={styles.infoItem}>
              <strong>Sede:</strong> {user.siteName}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Dashboard.displayName = 'Dashboard';
